import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./ErhWorkspace.css";
import { getLanguageCode } from "../../config/languages";

export type ErhWorkspaceRole = "school" | "admin" | "doctor";
type TimelineEvent = { event_id?: string; occurred_at?: string; text?: string };
type OperationalRequest = { request_id: string; request_type: string; status: string; requested_by_name?: string; requested_at?: string; assigned_role?: string; outcome?: string; method?: string; details?: string; video_room_id?: string };
type Ack = { role?: string; id?: string; name?: string; roe_version?: string; acknowledged_at?: string };
type ChatMessage = { id: string; from: "doctor" | "student" | "system"; text: string; ts?: number };
type ConsultMessage = { message_id: string; sender_id: string; sender_name: string; text: string; sent_at: string };
type AvailableAdvisor = { doctor_id?: string; id?: number; name?: string; specialties?: string; medical_field?: string; profession?: string; is_available?: boolean };
type Incident = {
  incident_id: string; status?: string; severity?: string; created_at?: string; org_code?: string;
  student_pk?: string; student_id?: string; grade?: string; student_language?: string; handled_by_id?: string; handled_by_name?: string;
  assigned_advisor_name?: string; latitude?: number; longitude?: number; location_accuracy?: number;
  location_updated_at?: string; location_status?: string; participants?: any; timeline?: TimelineEvent[];
  operational_requests?: OperationalRequest[]; roe_acknowledgements?: Ack[];
  student_chat_room_id?: string; consulting_advisor_id?: string; consulting_advisor_name?: string;
  consultation_status?: string; consultation_messages?: ConsultMessage[];
  context?: {
    category?: string;
    incident_type?: string;
    description?: string;
    description_english?: string;
    preferred_language?: string;
    reporter_language?: string;
    initiator_role?: string;
    reporter_role?: string;
    [key: string]: any;
  };
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000").replace(/\/+$/, "");
const roleLabel: Record<ErhWorkspaceRole, string> = { school: "School Administration", admin: "App Administration", doctor: "Attending Advisor" };

function getFirst(keys: string[]) { for (const store of [sessionStorage, localStorage]) for (const key of keys) { const v = store.getItem(key); if (v) return v; } return ""; }
function tokenFor(role: ErhWorkspaceRole) { return getFirst(role === "admin" ? ["admin_access_token","admin_token","access_token","token"] : role === "school" ? ["school_admin_token","school_access_token","school_token","access_token","token"] : ["doctor_access_token","doctor_token","access_token","token"]).replace(/^Bearer\s+/i, "").trim(); }
function identityFor(role: ErhWorkspaceRole) {
  const id =
    (role === "doctor"
      ? getFirst(["doctor_id","advisor_id","user_id"])
      : role === "school"
      ? getFirst(["school_admin_id","org_id","org_code"])
      : getFirst(["admin_id"])) || (role === "admin" ? "0" : "");
  const name = role === "doctor" ? getFirst(["doctor_name","advisor_name","name"]) : role === "school" ? getFirst(["school_admin_name","admin_name","contact_name"]) : getFirst(["admin_name","name"]);
  return { id, name };
}
function fmt(v?: string) { if (!v) return "—"; const d = new Date(v); return Number.isNaN(d.getTime()) ? v : d.toLocaleString(); }
function requestLabel(v: string) { return v.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()); }

async function translateForStudent(text: string, language: string): Promise<string> {
  const raw = String(text || "").trim();
  const lang = String(language || "English").trim() || "English";
  if (!raw || lang === "English") return raw;
  try {
    const r = await fetch(`${API_BASE}/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: raw, target: getLanguageCode(lang), source: "auto" }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return raw;
    return String(d?.translated_text || d?.translation || d?.text || raw);
  } catch {
    return raw;
  }
}

const ROE = [
  ["The Attending Advisor Leads", "The advisor connected to the user is the Incident Lead and determines what help is needed."],
  ["Do Not Assume", "School Admin and App Admin monitor and act only after a specific advisor request, except for an independently observed immediate emergency under established policy."],
  ["Request → Action → Confirmation", "Every operational action must be requested, acknowledged, performed, verified, reported back, and recorded."],
  ["Maintain Confidentiality", "The user-advisor conversation remains confidential. Administrators receive only the operational information necessary to coordinate safety."],
  ["Document Everything", "Requests, attempts, outcomes, location updates, responder participation, and closure decisions must be timestamped."],
  ["Protect Life First", "Protect life first and privacy whenever possible. Never report an action as completed until it is verified."],
  ["Advisor-Controlled Closure", "The attending advisor normally closes the incident after immediate safety is stabilized and outstanding actions are reviewed."],
];

export default function ErhWorkspace({ role }: { role: ErhWorkspaceRole }) {
  const { incidentId = "" } = useParams();
  const navigate = useNavigate();
  const ident = useMemo(() => identityFor(role), [role]);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [error, setError] = useState("");
  const [working, setWorking] = useState("");
  const roeStorageKey = useMemo(() => `erh_roe_ack:${incidentId}:${role}:${ident.id || "unknown"}`, [incidentId, role, ident.id]);
  const [roeOpen, setRoeOpen] = useState(() => {
    try { return localStorage.getItem(`erh_roe_ack:${incidentId}:${role}:${ident.id || "unknown"}`) !== "1"; }
    catch { return true; }
  });
  const [studentChat, setStudentChat] = useState<ChatMessage[]>([]);
  const [studentChatText, setStudentChatText] = useState("");
  const [studentChatConnected, setStudentChatConnected] = useState(false);
  const studentWsRef = useRef<WebSocket | null>(null);
  const studentRoomRef = useRef<string>("");
  const [availableAdvisors, setAvailableAdvisors] = useState<AvailableAdvisor[]>([]);
  const [selectedAdvisorId, setSelectedAdvisorId] = useState("");
  const [consultText, setConsultText] = useState("");
  const [pendingAssistance, setPendingAssistance] = useState<any | null>(null);
  const [schoolVideoRoomId, setSchoolVideoRoomId] = useState("");

  const load = useCallback(async () => {
    if (!incidentId || !ident.id) return;
    try {
      const token = tokenFor(role);
      const q = new URLSearchParams({ role, responder_id: ident.id });
      const r = await fetch(`${API_BASE}/realtime/erh/incidents/${encodeURIComponent(incidentId)}?${q}`, { cache: "no-store", headers: token ? { Authorization: `Bearer ${token}` } : undefined });
      const d = await r.json();
      if (!r.ok || d?.ok === false) throw new Error(d?.detail || `Request failed (${r.status})`);
      setIncident(d.incident);
      setError("");
    } catch (e: any) { setError(e?.message || "Unable to load AURA ERH incident."); }
  }, [incidentId, role, ident.id]);

  const loadAvailableAdvisors = useCallback(async () => {
    if (role !== "doctor" || !incident?.handled_by_id) return;
    try {
      const r = await fetch(`${API_BASE}/realtime/erh/incidents/${encodeURIComponent(incidentId)}/available-advisors`, { cache: "no-store" });
      const d = await r.json();
      if (r.ok && d?.ok !== false) setAvailableAdvisors(Array.isArray(d.advisors) ? d.advisors : []);
    } catch {}
  }, [incidentId, role, incident?.handled_by_id]);

  useEffect(() => {
    if (!incidentId || !ident.id) return;
    void load();
    const t = window.setInterval(() => void load(), 2500);
    return () => clearInterval(t);
  }, [incidentId, ident.id, load]);
  useEffect(() => { void loadAvailableAdvisors(); const t = window.setInterval(() => void loadAvailableAdvisors(), 6000); return () => clearInterval(t); }, [loadAvailableAdvisors]);
  useEffect(() => {
    if (role !== "doctor" || !ident.id) return;
    const check = async () => {
      try {
        const r = await fetch(`${API_BASE}/realtime/erh/assistance/pending?advisor_id=${encodeURIComponent(ident.id)}`, { cache: "no-store" });
        const d = await r.json();
        const rows = Array.isArray(d?.requests) ? d.requests : [];
        setPendingAssistance(rows.find((x:any) => String(x.incident_id) === String(incidentId)) || null);
      } catch {}
    };
    void check();
    const t = window.setInterval(() => void check(), 2500);
    return () => window.clearInterval(t);
  }, [incidentId, role, ident.id]);
  useEffect(() => {
    if (!incident || !ident.id) return;
    const acked = (incident.roe_acknowledgements || []).some(
      a => String(a.role) === role && String(a.id) === String(ident.id)
    );
    let locallyAcked = false;
    try { locallyAcked = localStorage.getItem(roeStorageKey) === "1"; } catch {}
    if (acked || locallyAcked) setRoeOpen(false);
  }, [incident, ident.id, role, roeStorageKey]);
  const call = useCallback(async (path: string, params: Record<string,string>) => {
    setWorking(path);
    try {
      const token = tokenFor(role);
      const q = new URLSearchParams(params);
      const r = await fetch(`${API_BASE}${path}?${q}`, { method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : undefined });
      const d = await r.json();
      if (!r.ok || d?.ok === false) throw new Error(d?.detail || "ERH action failed");
      await load();
      return true;
    } catch(e:any){ setError(e?.message || "ERH action failed"); return false; }
    finally { setWorking(""); }
  }, [load, role]);

  useEffect(() => {
    if (!incidentId || (role !== "school" && role !== "admin")) return;
    const heartbeat = () => void call(`/realtime/erh/incidents/${encodeURIComponent(incidentId)}/monitor`, { role, responder_id: ident.id, responder_name: ident.name });
    heartbeat();
    const timer = window.setInterval(heartbeat, 15000);
    return () => window.clearInterval(timer);
  }, [incidentId, role, ident.id, ident.name, call]);

  useEffect(() => {
    const isPrimaryAdvisor =
      role === "doctor" &&
      !!incidentId &&
      !!incident?.student_chat_room_id &&
      String(incident?.handled_by_id || "") === String(ident.id);

    if (!isPrimaryAdvisor) {
      try { studentWsRef.current?.close(); } catch {}
      studentWsRef.current = null;
      studentRoomRef.current = "";
      setStudentChatConnected(false);
      return;
    }

    const roomId = String(incident?.student_chat_room_id || "").trim();
    if (!roomId) return;

    try {
      const history = JSON.parse(localStorage.getItem(`erh_chat_history:${incidentId}`) || "[]");
      if (Array.isArray(history)) setStudentChat(history);
    } catch {}

    const wsBase = API_BASE.startsWith("https://")
      ? API_BASE.replace(/^https:/, "wss:")
      : API_BASE.replace(/^http:/, "ws:");

    const params = new URLSearchParams({
      role: "erh_doctor",
      doctor_id: String(ident.id),
      name: String(ident.name || ""),
      display_name: String(ident.name || ""),
      incident_id: String(incidentId),
    });

    const socket = new WebSocket(`${wsBase}/realtime/ws?${params.toString()}`);
    studentWsRef.current = socket;
    studentRoomRef.current = roomId;

    socket.onopen = () => {
      if (studentWsRef.current !== socket) return;
      socket.send(JSON.stringify({ type: "chat.join", room_id: roomId }));
      setStudentChatConnected(true);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data || "{}");
        if (data?.type !== "chat.message") return;
        if (String(data?.room_id || "") !== roomId) return;

        const text = String(data?.text || "").trim();
        if (!text) return;

        const message: ChatMessage = {
          id: String(data?.server_msg_id || data?.client_msg_id || `${Date.now()}-${Math.random()}`),
          from: String(data?.from || "") === "student" ? "student" : "doctor",
          text,
          ts: Number(data?.ts || Date.now()),
        };

        setStudentChat(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          const next = [...prev, message].slice(-200);
          try { localStorage.setItem(`erh_chat_history:${incidentId}`, JSON.stringify(next)); } catch {}
          return next;
        });
      } catch {}
    };

    socket.onerror = () => {
      if (studentWsRef.current === socket) setStudentChatConnected(false);
    };

    socket.onclose = () => {
      if (studentWsRef.current === socket) {
        setStudentChatConnected(false);
        studentWsRef.current = null;
      }
    };

    return () => {
      if (studentWsRef.current === socket) studentWsRef.current = null;
      try { socket.close(); } catch {}
      studentRoomRef.current = "";
      setStudentChatConnected(false);
    };
  }, [
    incidentId,
    role,
    ident.id,
    ident.name,
    incident?.handled_by_id,
    incident?.student_chat_room_id,
  ]);

  const acknowledge = async () => {
    setRoeOpen(false);
    setWorking("roe-acknowledge");
    try {
      const token = tokenFor(role);
      const q = new URLSearchParams({
        role,
        responder_id: ident.id,
        responder_name: ident.name,
      });
      const r = await fetch(
        `${API_BASE}/realtime/erh/incidents/${encodeURIComponent(incidentId)}/roe/acknowledge?${q}`,
        { method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.ok === false) throw new Error(d?.detail || `ROE acknowledgement failed (${r.status})`);
      try { localStorage.setItem(roeStorageKey, "1"); } catch {}
      await load();
    } catch (e: any) {
      setError(e?.message || "Unable to acknowledge Rules of Engagement.");
      setRoeOpen(true);
    } finally {
      setWorking("");
    }
  };
  const accept = () => call(`/realtime/sos/${encodeURIComponent(incidentId)}/accept`, { role, responder_id: ident.id, responder_name: ident.name });
  const closeIncident = () => call(`/realtime/sos/${encodeURIComponent(incidentId)}/close`, { role, responder_id: ident.id });
  const createRequest = (type: string, assigned_role = "school") => call(`/realtime/erh/incidents/${encodeURIComponent(incidentId)}/requests`, { request_type: type, responder_id: ident.id, responder_name: ident.name, assigned_role });
  const refreshGps = () => call(`/realtime/erh/incidents/${encodeURIComponent(incidentId)}/location/refresh`, { role, responder_id: ident.id });
  const openErhVideo = (request: OperationalRequest) => {
    const roomId = String(request.video_room_id || `erh-video:${incidentId}:${request.request_id}`);
    if (role === "doctor" && String(incident?.handled_by_id || "") === String(ident.id)) {
      try {
        localStorage.setItem("erh_video_open", JSON.stringify({
          room_id: roomId,
          advisor_id: ident.id,
          incident_id: incidentId,
          ts: Date.now()
        }));
      } catch {}
      window.alert("ERH Video Room opened in your active Advisor Portal tab.");
      return;
    }
    if (role === "school") setSchoolVideoRoomId(roomId);
  };
  const updateRequest = (r: OperationalRequest, status: string) => {
    let outcome = "", method = "", details = "";
    if (["completed","unable_to_complete","delayed"].includes(status)) { outcome = window.prompt("Verified outcome:", status === "completed" ? "Successfully completed" : "") || ""; method = window.prompt("Method used (phone, text, in person, etc.):", "") || ""; details = window.prompt("Operational note (no confidential chat content):", "") || ""; }
    return call(`/realtime/erh/incidents/${encodeURIComponent(incidentId)}/requests/${encodeURIComponent(r.request_id)}`, { role, responder_id: ident.id, responder_name: ident.name, status, outcome, method, details });
  };

  const sendStudentChat = async () => {
    const text = studentChatText.trim();
    const socket = studentWsRef.current;
    const roomId = studentRoomRef.current;
    if (!text || !socket || socket.readyState !== WebSocket.OPEN || !roomId) return;

    const clientMsgId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const studentLanguage = String(incident?.student_language || "English").trim() || "English";
    const outgoingText = await translateForStudent(text, studentLanguage);

    const message: ChatMessage = {
      id: clientMsgId,
      from: "doctor",
      text,
      ts: Date.now(),
    };

    setStudentChat(prev => {
      const next = [...prev, message].slice(-200);
      try { localStorage.setItem(`erh_chat_history:${incidentId}`, JSON.stringify(next)); } catch {}
      return next;
    });

    socket.send(JSON.stringify({
      type: "chat.message",
      room_id: roomId,
      text: outgoingText || text,
      client_msg_id: clientMsgId,
      student_language: studentLanguage,
    }));
    setStudentChatText("");
  };
  const requestAssistance = (targetAdvisorId = "") => call(`/realtime/erh/incidents/${encodeURIComponent(incidentId)}/assistance/request`, { responder_id: ident.id, responder_name: ident.name, target_advisor_id: targetAdvisorId });
  const acceptAssistance = async () => {
    const ok = await call(`/realtime/erh/incidents/${encodeURIComponent(incidentId)}/assistance/accept`, { responder_id: ident.id, responder_name: ident.name });
    if (ok) setPendingAssistance(null);
  };
  const sendConsultMessage = async () => {
    const text = consultText.trim();
    if (!text) return;
    const ok = await call(`/realtime/erh/incidents/${encodeURIComponent(incidentId)}/consultation/messages`, { responder_id: ident.id, responder_name: ident.name, text });
    if (ok) setConsultText("");
  };

  const latitude = Number(incident?.latitude); const longitude = Number(incident?.longitude);
  const locationReady = Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 0 && longitude === 0);
  const roeAcked = (incident?.roe_acknowledgements || []).some(a => String(a.role) === role && String(a.id) === String(ident.id));
  const advisorName = incident?.handled_by_name || incident?.assigned_advisor_name || incident?.participants?.attending_advisor?.name || (incident?.handled_by_id ? `Advisor ${incident.handled_by_id}` : "Awaiting assignment");
  const schoolMonitoring = !!incident?.participants?.school_admin?.monitoring;
  const appMonitoring = !!incident?.participants?.app_admin?.monitoring;
  const isLeadAdvisor = role === "doctor" && String(incident?.handled_by_id || "") === String(ident.id);
  const isConsultingAdvisor = role === "doctor" && String(incident?.consulting_advisor_id || "") === String(ident.id);
  const consultMessages = incident?.consultation_messages || [];
  const incidentContext = incident?.context || {};
  const reporterRole = String(
    incidentContext.reporter_role || incidentContext.initiator_role || ""
  ).trim().toLowerCase();
  const reporterLabel =
    reporterRole === "parent"
      ? "Parent"
      : reporterRole === "teacher"
        ? "Teacher"
        : "User";
  const reportedCategory = String(
    incidentContext.category || incidentContext.incident_type || ""
  ).trim();
  const reportedDescription = String(
    incidentContext.description_english || incidentContext.description || ""
  ).trim();

  return <div className="erh-page"><div className="erh-shell">
    <header className="erh-topbar"><div><div className="erh-brand">AURA ERH™</div><div className="erh-subtitle">Emergency Response Hub • {roleLabel[role]}</div></div><div className="erh-top-actions"><button onClick={() => setRoeOpen(true)}>Rules of Engagement</button><button onClick={() => navigate(-1)}>Back</button><button onClick={() => window.close()}>Close Window</button></div></header>
    <div className="erh-doctrine">One incident. One command record. Separate confidential channels for the student intervention and advisor consultation.</div>
    {error && <div className="erh-error">{error}</div>}
    {!incident ? <section className="erh-empty"><h2>Incident not found</h2></section> : <>
      <section className="erh-incident-banner"><div><div className="erh-kicker">Active Incident</div><h1>{incident.incident_id}</h1></div><div className={`erh-severity ${String(incident.severity || "critical").toLowerCase()}`}>{String(incident.severity || "critical").toUpperCase()}</div><div className="erh-status">{String(incident.status || "open").toUpperCase()}</div></section>
      <section className="erh-command-strip">
        <Status ok label="Student Connected"/><Status ok={!!incident.handled_by_id} label={`Attending Advisor: ${advisorName}`}/><Status ok={locationReady} label={locationReady ? "GPS Located" : `GPS ${requestLabel(incident.location_status || "pending")}`}/><Status ok={schoolMonitoring} label="School Admin Monitoring"/><Status ok={appMonitoring} label="App Admin Monitoring"/><Status ok={roeAcked} label="ROE Acknowledged"/>
      </section>
      <main className="erh-grid">
        <section className="erh-card"><h2>Incident Management</h2><dl className="erh-details"><div><dt>Organization</dt><dd>{incident.org_code || "Pending"}</dd></div><div><dt>User ID</dt><dd>{incident.student_id || incident.student_pk || "Protected"}</dd></div><div><dt>Grade</dt><dd>{incident.grade || "—"}</dd></div><div><dt>Student Language</dt><dd>{incident.student_language || "English"}</dd></div><div><dt>Opened</dt><dd>{fmt(incident.created_at)}</dd></div><div><dt>Incident Lead</dt><dd>{advisorName}</dd></div></dl>{role === "school" && <button className="erh-secondary-action" disabled={!roeAcked}>Identify User from Protected Roster</button>}</section>
        <section className="erh-card"><h2>Location Services</h2><div className={`erh-service-status ${locationReady ? "ready" : "pending"}`}>{locationReady ? "User-device GPS available" : incident.location_status === "denied" ? "Location permission denied" : incident.location_status === "timeout" ? "Location request timed out" : incident.location_status === "unavailable" ? "Device location unavailable" : incident.location_status === "refresh_requested" ? "Fresh GPS location requested…" : "Waiting for user-device GPS"}</div>{locationReady && <><div className="erh-location-copy">{latitude.toFixed(6)}, {longitude.toFixed(6)}{incident.location_accuracy ? ` • Accuracy ${Math.round(incident.location_accuracy)} m` : ""}<br/>{incident.location_updated_at ? `Captured ${fmt(incident.location_updated_at)}` : ""}</div><a className="erh-primary-action" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${latitude},${longitude}`}>Open Exact Map Location</a></>}{["doctor","school","admin"].includes(role) && <button className="erh-primary-action" disabled={!!working} onClick={() => void refreshGps()}>{locationReady ? "Refresh GPS Location" : "Retry GPS Location"}</button>}</section>
        <section className="erh-card"><h2>Participants & Presence</h2><ul className="erh-presence-list"><li><span className={incident.handled_by_id ? "online" : "pending"}/>Attending Advisor: {advisorName}</li><li><span className={schoolMonitoring ? "online" : "pending"}/>School Admin: {schoolMonitoring ? "Monitoring" : "Not yet monitoring"}</li><li><span className={appMonitoring ? "online" : "pending"}/>App Admin: {appMonitoring ? "Monitoring" : "Not yet monitoring"}</li><li><span className={incident.consulting_advisor_id ? "online" : "pending"}/>Consulting Advisor: {incident.consulting_advisor_name || "Not connected"}</li></ul><div className="erh-privacy-note">Administrators can see operational status only. They cannot see either confidential chat channel.</div></section>

        {role === "doctor" && (reportedCategory || reportedDescription) && <section className="erh-card"><h2>Reported Emergency</h2><dl className="erh-details">{reportedCategory ? <div><dt>Crisis Type</dt><dd>{reportedCategory}</dd></div> : null}<div><dt>Reported By</dt><dd>{reporterLabel}</dd></div>{reportedDescription ? <div><dt>Initial Report</dt><dd>{reportedDescription}</dd></div> : null}</dl></section>}

        {role === "doctor" && isLeadAdvisor && <section className="erh-card erh-chat-card"><h2>{reporterRole === "parent" ? "Confidential Parent Intervention Chat" : reporterRole === "teacher" ? "Confidential Teacher Intervention Chat" : "Confidential Student Intervention Chat"}</h2><div className={`erh-service-status ${studentChatConnected ? "ready" : "pending"}`}>{studentChatConnected ? `Live ${reporterRole === "parent" ? "parent" : reporterRole === "teacher" ? "teacher" : "student"} connection active` : `Connecting directly to ${reporterRole === "parent" ? "parent" : reporterRole === "teacher" ? "teacher" : "student"} session…`}</div><div className="erh-chat-thread">{studentChat.length ? studentChat.map(m => <div key={m.id} className={`erh-chat-message ${m.from}`}>{m.text}</div>) : <div className="erh-shell-note">Connecting the ERH command center directly to the active student session.</div>}</div><div className="erh-chat-composer"><input value={studentChatText} onChange={e => setStudentChatText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); sendStudentChat(); } }} placeholder="Message the student…"/><button disabled={!studentChatConnected} onClick={sendStudentChat}>Send</button></div></section>}

        {role === "doctor" && <section className="erh-card erh-assistance-card"><h2>Advisor Assistance</h2>{pendingAssistance && !isLeadAdvisor && !isConsultingAdvisor ? <div className="erh-service-status pending"><strong>Private Consulting Assistance Requested</strong><br/>Incident Lead: {pendingAssistance.incident_lead_name || "Attending Advisor"}<br/><button className="erh-primary-action" disabled={!!working} onClick={() => void acceptAssistance()}>Accept & Join as Consulting Advisor</button></div> : null}{isLeadAdvisor ? <><div className="erh-assistance-actions"><button onClick={() => void requestAssistance("")}>Broadcast Need Assistance</button><select value={selectedAdvisorId} onChange={e => setSelectedAdvisorId(e.target.value)}><option value="">Select an available advisor…</option>{availableAdvisors.map(a => <option key={String(a.doctor_id || a.id)} value={String(a.doctor_id || a.id)}>{a.name || `Advisor ${a.doctor_id || a.id}`} — {a.specialties || a.medical_field || a.profession || "Advisor"}</option>)}</select><button disabled={!selectedAdvisorId} onClick={() => void requestAssistance(selectedAdvisorId)}>Invite Selected Advisor</button></div></> : <p className="erh-shell-note">Only the Attending Advisor can request consulting assistance.</p>}<div className="erh-privacy-note">School Admin, App Admin, and the student see only that a consulting advisor joined—not the consultation content.</div></section>}

        {role === "doctor" && (isLeadAdvisor || isConsultingAdvisor) && <section className="erh-card erh-consult-card"><h2>Private Advisor Consultation — Advisor-to-Advisor Only</h2><div className="erh-service-status ready">{incident.consulting_advisor_name ? `Connected with ${incident.consulting_advisor_name}` : "Waiting for a consulting advisor to accept"}</div><div className="erh-consult-thread">{consultMessages.length ? consultMessages.map(m => <div key={m.message_id} className={`erh-consult-message ${String(m.sender_id) === String(ident.id) ? "mine" : "theirs"}`}><strong>{m.sender_name}</strong><span>{m.text}</span><small>{fmt(m.sent_at)}</small></div>) : <div className="erh-shell-note">No private consultation messages yet.</div>}</div><div className="erh-chat-composer"><input value={consultText} onChange={e => setConsultText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); void sendConsultMessage(); } }} placeholder="PRIVATE — message consulting advisor only…"/><button disabled={!incident.consulting_advisor_id} onClick={() => void sendConsultMessage()}>Send to Consulting Advisor</button></div></section>}

        <section className="erh-card erh-requests"><h2>Operational Requests</h2>{role === "doctor" && isLeadAdvisor ? <div className="erh-request-grid">{[["parent_notification","Parent / Guardian Notification","school"],["school_crisis_team","School Crisis Team","school"],["welfare_check","Welfare Check","school"],["gps_assistance","Help at GPS Location","school"]].map(([type,label,assigned]) => <button disabled={!roeAcked || !incident.handled_by_id} key={type} onClick={() => void createRequest(type, assigned)}>Request {label}</button>)}</div> : <p className="erh-shell-note">Administrators monitor. Action controls appear only after the attending advisor submits a request.</p>}<div className="erh-request-list">{(incident.operational_requests || []).map(r => <div className="erh-request-row" key={r.request_id}><div><strong>{requestLabel(r.request_type)}</strong><small>Requested by {r.requested_by_name || "Attending Advisor"} • {fmt(r.requested_at)}</small></div><span className={`request-status ${r.status}`}>{requestLabel(r.status)}</span>{role !== "doctor" && r.assigned_role === role && !["completed","unable_to_complete","cancelled"].includes(r.status) ? <div className="request-actions"><button disabled={!roeAcked} onClick={() => void updateRequest(r,"acknowledged")}>Acknowledge</button><button disabled={!roeAcked} onClick={() => void updateRequest(r,"in_progress")}>In Progress</button><button disabled={!roeAcked} onClick={() => void updateRequest(r,"completed")}>Confirm Completed</button><button disabled={!roeAcked} onClick={() => void updateRequest(r,"unable_to_complete")}>Unable to Complete</button></div> : null}{null}{r.outcome && <p>{r.outcome}{r.method ? ` • ${r.method}` : ""}</p>}</div>)}</div>{role === "school" && schoolVideoRoomId ? <SchoolErhVideoRoom roomId={schoolVideoRoomId} schoolId={ident.id} onHangup={() => setSchoolVideoRoomId("")}/> : null}</section>
        <section className="erh-card erh-timeline-card"><h2>Incident Timeline</h2><div className="erh-timeline">{(incident.timeline || []).map((e,i) => <div key={e.event_id || i}><time>{e.occurred_at ? new Date(e.occurred_at).toLocaleTimeString() : "Now"}</time><span>{e.text || "Incident updated"}</span></div>)}</div></section>
        <section className="erh-card"><h2>Incident Control</h2><div className="erh-control-stack">{role === "doctor" && String(incident.status || "open").toLowerCase() === "open" && <button className="erh-primary-action" disabled={!roeAcked || !!working} onClick={() => void accept()}>Accept & Lead Response</button>}{role === "doctor" && isLeadAdvisor && <button className="erh-danger-action" disabled={!roeAcked || !!working} onClick={() => void closeIncident()}>Close Incident</button>}</div></section>
      </main>
    </>}
  </div>{roeOpen && <div className="roe-overlay"><div className="roe-modal"><div className="roe-kicker">AURA ERH™ Protocol 1.0</div><h2>Rules of Engagement</h2><p className="roe-intro">Read these instructions before participating in this incident.</p>{ROE.map(([h,b]) => <section key={h}><h3>{h}</h3><p>{b}</p></section>)}<div className="roe-roles"><b>Attending Advisor:</b> Incident Lead • <b>School Admin:</b> Operational Support • <b>App Admin:</b> System Support • <b>Consulting Advisor:</b> Private Consultation</div><button className="roe-ack" disabled={!!working} onClick={() => void acknowledge()}>I Have Read, Understand, and Agree to Follow These Rules</button></div></div>}</div>;
}

function SchoolErhVideoRoom({ roomId, schoolId, onHangup }: { roomId: string; schoolId: string; onHangup: () => void }) {
  const localVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const pendingIceRef = React.useRef<RTCIceCandidateInit[]>([]);
  const [status, setStatus] = React.useState("Connecting…");

  React.useEffect(() => {
    let cancelled = false;
    const base = API_BASE.replace(/\/+$/, "");
    const wsBase = base.startsWith("https://") ? base.replace(/^https:/, "wss:") : base.replace(/^http:/, "ws:");
    const orgCode = String(localStorage.getItem("org_code") || sessionStorage.getItem("org_code") || schoolId || "");
    const qp = new URLSearchParams({
      role: "school",
      school_id: `${schoolId}:erhvideo:${roomId}`,
      org_code: orgCode,
      display_name: "School Admin ERH Video",
    });
    const socket = new WebSocket(`${wsBase}/realtime/ws?${qp.toString()}`);
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });

    pc.ontrack = (event) => {
      const stream = event.streams?.[0];
      if (stream && remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
    };
    pc.onicecandidate = (event) => {
      if (!event.candidate || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: "rtc.ice", room_id: roomId, candidate: event.candidate, payload: { room_id: roomId, candidate: event.candidate } }));
    };
    pc.onconnectionstatechange = () => {
      if (!cancelled && pc.connectionState === "connected") setStatus("Connected ✅");
    };

    const flushIce = async () => {
      if (!pc.remoteDescription) return;
      for (const candidate of pendingIceRef.current.splice(0)) {
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
      }
    };

    socket.onopen = async () => {
      try {
        const media = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) return;
        streamRef.current = media;
        if (localVideoRef.current) localVideoRef.current.srcObject = media;
        media.getTracks().forEach(track => pc.addTrack(track, media));
        socket.send(JSON.stringify({ type: "chat.join", room_id: roomId }));
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.send(JSON.stringify({ type: "rtc.offer", room_id: roomId, sdp: offer, payload: { room_id: roomId, sdp: offer } }));
        setStatus("Waiting for Attending Advisor…");
      } catch (e:any) {
        setStatus(e?.message || "Unable to open video");
      }
    };

    socket.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        const payload = data?.payload || data;
        const rid = String(payload?.room_id || data?.room_id || "");
        if (rid !== roomId) return;
        if (data.type === "rtc.answer" && payload?.sdp) {
          await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
          await flushIce();
        } else if (data.type === "rtc.ice" && payload?.candidate) {
          if (!pc.remoteDescription) pendingIceRef.current.push(payload.candidate);
          else {
            try { await pc.addIceCandidate(new RTCIceCandidate(payload.candidate)); } catch {}
          }
        } else if (data.type === "rtc.hangup") {
          onHangup();
        }
      } catch {}
    };

    return () => {
      cancelled = true;
      try {
        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "rtc.hangup", room_id: roomId, payload: { room_id: roomId } }));
      } catch {}
      try { socket.close(); } catch {}
      try { pc.close(); } catch {}
      try { streamRef.current?.getTracks().forEach(track => track.stop()); } catch {}
    };
  }, [roomId, schoolId, onHangup]);

  return <div className="erh-video-room"><div className="erh-service-status ready"><strong>ERH Video Room</strong> — {status}</div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:10}}><video ref={localVideoRef} autoPlay playsInline muted style={{width:"100%",minHeight:180,background:"#000",borderRadius:10}}/><video ref={remoteVideoRef} autoPlay playsInline style={{width:"100%",minHeight:180,background:"#000",borderRadius:10}}/></div><button className="erh-primary-action" onClick={onHangup}>End ERH Video</button></div>;
}

function Status({ok,label}:{ok:boolean,label:string}) { return <div className={`command-status ${ok ? "ok" : "wait"}`}><span/>{label}</div>; }